import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { barsApi } from '@/api/endpoints/bars';
import { PageSpinner, Card, CardContent, Button } from '@/components/ui';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';

export function HiddenBarsPage() {
  const { hidden, toggleHidden } = useUserPreferences();
  const hiddenIds = useMemo(() => Array.from(hidden), [hidden]);

  // Fetch all hidden bars
  const barQueries = useQueries({
    queries: hiddenIds.map((id) => ({
      queryKey: ['bar', id],
      queryFn: () => barsApi.getById(id),
    })),
  });

  const isLoading = barQueries.some((q) => q.isLoading);
  const bars = barQueries
    .filter((q) => q.data?.bar)
    .map((q) => q.data!.bar);

  if (isLoading && hiddenIds.length > 0) {
    return <PageSpinner />;
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4">
      <div className="mx-auto max-w-lg">
        {/* Back link */}
        <Link
          to="/settings"
          className="mb-6 flex items-center gap-2 text-sm text-[var(--color-muted)] hover:text-[var(--color-text)]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to settings
        </Link>

        <h1 className="mb-6 text-2xl font-bold">Hidden Bars</h1>

        {/* Hidden bars list */}
        {bars.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center py-12 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--color-border)]">
                <EyeOff className="h-8 w-8 text-[var(--color-muted)]" />
              </div>
              <h2 className="mt-4 text-lg font-semibold">No hidden bars</h2>
              <p className="mt-2 max-w-sm text-sm text-[var(--color-muted)]">
                Bars you hide will appear here. You can unhide them at any time.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {bars.map((bar) => (
              <Card key={bar.id}>
                <CardContent className="flex items-center justify-between p-4">
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-medium">{bar.name}</h3>
                    {bar.address && (
                      <p className="truncate text-sm text-[var(--color-muted)]">
                        {bar.address}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => toggleHidden(bar.id)}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    Unhide
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
